/**
 * /broadcasts/new — compose a broadcast. Cycle 12.
 *
 * Picks segment + IG account → composes body → optional message_tag →
 * shows estimate-eligible-recipients → confirm dialog → POST /broadcasts
 * → POST /broadcasts/:id/send. Schedule-for-future is wired but
 * defaults to now.
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/auth-context.js";

interface SegmentRow {
  id: string;
  name: string;
}

const TAG_OPTIONS: { value: string; label: string; help: string }[] = [
  { value: "", label: "(none — must be inside 24h window)", help: "Default. Send-gate enforces the 24h rule per recipient. Recipients outside the window will be skipped." },
  { value: "ACCOUNT_UPDATE", label: "ACCOUNT_UPDATE", help: "Bypasses the 24h window. Use only for non-recurring updates that change a user's account state. You are responsible for ensuring the message qualifies under Meta policy." },
];

export const Route = createFileRoute("/_app/broadcasts/new")({
  component: NewBroadcastPage,
});

function NewBroadcastPage() {
  const { activeIgAccount } = useAuth();
  const navigate = useNavigate();
  const [segments, setSegments] = useState<SegmentRow[]>([]);
  const [name, setName] = useState("");
  const [segmentId, setSegmentId] = useState("");
  const [text, setText] = useState("");
  const [messageTag, setMessageTag] = useState("");
  const [estimate, setEstimate] = useState<number | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void loadSegments();
  }, []);

  async function loadSegments() {
    try {
      const r = await api.get<{ segments: SegmentRow[] }>("/segments");
      setSegments(r.data.segments);
    } catch {
      setError("Failed to load segments");
    }
  }

  // Re-estimate whenever segment + account change.
  useEffect(() => {
    if (!segmentId || !activeIgAccount) {
      setEstimate(null);
      return;
    }
    setEstimating(true);
    setError(null);
    const ctl = new AbortController();
    api
      .post<{ count: number; capped: boolean }>(
        "/broadcasts/estimate",
        { segmentId, igAccountId: activeIgAccount.id },
        { signal: ctl.signal },
      )
      .then((r) => setEstimate(r.data.count))
      .catch((err) => {
        if (ctl.signal.aborted) return;
        setEstimate(null);
        setError(
          (err as { response?: { data?: { message?: string } } })
            .response?.data?.message ?? "estimate failed",
        );
      })
      .finally(() => setEstimating(false));
    return () => ctl.abort();
  }, [segmentId, activeIgAccount?.id]);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !segmentId || !activeIgAccount || !text.trim()) return;
    setConfirmOpen(true);
  }

  async function reallySend() {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<{ broadcast: { id: string } }>("/broadcasts", {
        name: name.trim(),
        segmentId,
        igAccountId: activeIgAccount!.id,
        body: { text: text.trim() },
        messageTag: messageTag || null,
      });
      const id = created.data.broadcast.id;
      await api.post(`/broadcasts/${id}/send`, {});
      void navigate({ to: "/broadcasts/$broadcastId", params: { broadcastId: id } });
    } catch (err) {
      setError(
        (err as { response?: { data?: { message?: string } } })
          .response?.data?.message ?? "Failed",
      );
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  const tag = TAG_OPTIONS.find((t) => t.value === messageTag);

  return (
    <section style={{ maxWidth: 720, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 24px", fontSize: 28 }}>New broadcast</h1>

      <div style={{ fontSize: 13, marginBottom: 12, color: "var(--text-muted)" }}>
        Posting from <strong>@{activeIgAccount?.igUsername}</strong>
      </div>

      <form onSubmit={send} style={{ display: "grid", gap: 14 }}>
        <div className="brut-card" style={{ padding: 14, display: "grid", gap: 10 }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Broadcast name"
            className="brut-input"
            maxLength={120}
            required
          />
          <label className="brut-label">Segment</label>
          <select
            value={segmentId}
            onChange={(e) => setSegmentId(e.target.value)}
            className="brut-input"
            required
          >
            <option value="">— pick a segment —</option>
            {segments.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="brut-card" style={{ padding: 14, display: "grid", gap: 10 }}>
          <label className="brut-label">Message body</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="brut-input"
            rows={5}
            maxLength={1024}
            required
            placeholder="What you want to send to every recipient."
          />
          <label className="brut-label">Message tag (Meta policy)</label>
          <select
            value={messageTag}
            onChange={(e) => setMessageTag(e.target.value)}
            className="brut-input"
          >
            {TAG_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {tag ? (
            <div style={{ fontSize: 12, color: "var(--muted)" }}>{tag.help}</div>
          ) : null}
        </div>

        <div
          className="brut-card"
          style={{ padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <div>
            {estimating ? (
              <span style={{ color: "var(--muted)" }}>Counting recipients…</span>
            ) : estimate !== null ? (
              <strong>~{estimate.toLocaleString()} eligible recipients</strong>
            ) : (
              <span style={{ color: "var(--muted)" }}>
                Pick a segment + account to estimate
              </span>
            )}
          </div>
          <button
            type="submit"
            className="brut-button"
            disabled={!name.trim() || !segmentId || !activeIgAccount || !text.trim() || busy}
          >
            Send broadcast
          </button>
        </div>

        {error ? (
          <div className="brut-card" style={{ background: "#ffd7d7", padding: 10 }}>
            {error}
          </div>
        ) : null}
      </form>

      {confirmOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div className="brut-card" style={{ padding: 20, maxWidth: 480, background: "var(--bg)" }}>
            <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>Send broadcast?</h2>
            <p>
              <strong>{estimate?.toLocaleString() ?? "?"}</strong> recipients will
              receive this message. Skipped recipients (outside 24h window, opted
              out, etc.) appear in the ledger after sending.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="brut-button is-secondary"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void reallySend()}
                className="brut-button"
                disabled={busy}
              >
                {busy ? "Starting…" : "Send now"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
